/* eslint-disable */
// @ts-nocheck
/**
 * Ultra-fast frame management utilities using binary search
 * Provides O(log n) insertion instead of O(n log n) sorting
 */

export interface FrameData {
  globalFrameIndex?: number;
  clipIndex?: number;
  [key: string]: any;
}

export interface ClipInfo {
  index: number;
  frameCount: number;
  [key: string]: any;
}

/**
 * Binary search to find duplicate frame by globalFrameIndex
 * @param arr - Sorted array of frames
 * @param targetIndex - globalFrameIndex to search for
 * @returns Index of found frame or -1 if not found
 */
function binarySearchFrame(arr: FrameData[], targetIndex: number): number {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midIndex = arr[mid].globalFrameIndex ?? 0;

    if (midIndex === targetIndex) {
      return mid; // Found duplicate
    } else if (midIndex < targetIndex) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return -1; // Not found
}

/**
 * Binary search to find insertion point for maintaining sort order
 * @param arr - Sorted array of frames
 * @param targetIndex - globalFrameIndex to insert
 * @returns Index where frame should be inserted
 */
function binarySearchInsertionPoint(
  arr: FrameData[],
  targetIndex: number
): number {
  let left = 0;
  let right = arr.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midIndex = arr[mid].globalFrameIndex ?? 0;

    if (midIndex < targetIndex) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  return left;
}

/**
 * Insert new frames into sorted array using binary search for O(log n) performance
 * Automatically handles duplicates and maintains sort order
 * @param existingFrames - Current sorted frame array
 * @param newFrames - New frames to insert
 * @returns Updated sorted array
 */
export function insertFramesOptimized(
  existingFrames: FrameData[],
  newFrames: FrameData[]
): FrameData[] {
  // Start with existing store (already sorted)
  const updatedStore = [...existingFrames];

  // Insert each new frame using binary search - O(log n) per frame
  newFrames.forEach((newFrame) => {
    const globalIndex = newFrame.globalFrameIndex ?? 0;

    // Quick duplicate check using binary search
    const existingIndex = binarySearchFrame(updatedStore, globalIndex);
    if (existingIndex !== -1) {
      return; // Skip duplicates
    }

    // Binary search for insertion point
    const insertIndex = binarySearchInsertionPoint(updatedStore, globalIndex);
    updatedStore.splice(insertIndex, 0, newFrame);
  });

  return updatedStore;
}

/**
 * Release complete clips from buffer when all frames are available
 * @param buffer - Map of clip index to frame arrays
 * @param clips - Clip metadata with expected frame counts
 * @returns Object with releasable frames and processed clip indices
 */
export function releaseBufferedFrames(
  buffer: Map<number, any[]>,
  clips: ClipInfo[]
) {
  const releasableFrames: any[] = [];
  const processedClips: number[] = [];

  // Get all available clips from buffer
  const availableClipIndices = Array.from(buffer.keys()).sort((a, b) => a - b);

  // Release any clips that are complete, not just sequential ones
  for (const clipIndex of availableClipIndices) {
    const clipFrames = buffer.get(clipIndex);
    if (clipFrames && clipFrames.length > 0) {
      // Find the corresponding clip to get expected frame count
      const clipInfo = clips.find((c) => c.index === clipIndex);
      const expectedFrameCount = clipInfo ? clipInfo.frameCount : 36; // Default to 36 if not found

      // Only release if we have all frames for this clip
      if (clipFrames.length >= expectedFrameCount) {
        releasableFrames.push(...clipFrames);
        processedClips.push(clipIndex);
      }
    }
  }

  // Sort all releasable frames by their global index
  releasableFrames.sort((a, b) => {
    const globalA = a.globalFrameIndex ?? 0;
    const globalB = b.globalFrameIndex ?? 0;
    return globalA - globalB;
  });

  return {releasableFrames, processedClips};
}

/**
 * Find the closest available frame to a target index
 * @param frameMap - Map of frame indices to frame data
 * @param targetIndex - Target frame index to find closest match for
 * @returns Closest frame data or null if none available
 */
export function findClosestFrame(
  frameMap: Map<number, any>,
  targetIndex: number
) {
  const availableIndices = Array.from(frameMap.keys()).sort((a, b) => a - b);
  if (availableIndices.length === 0) return null;

  let closest = availableIndices[0];
  let minDistance = Math.abs(targetIndex - closest);

  for (const index of availableIndices) {
    const distance = Math.abs(targetIndex - index);
    if (distance < minDistance) {
      minDistance = distance;
      closest = index;
    }
  }

  return frameMap.get(closest);
}

/**
 * Find the closest available frame in a sparse frame array
 * Uses an expanding search radius to find the nearest frame
 * @param framesStore - Sparse array of frame data
 * @param targetIndex - Target frame index
 * @param maxSearchRadius - Maximum distance to search (default: 50)
 * @returns Object with frame data and index, or null if not found
 */
export function findClosestFrameInArray(
  framesStore: any[],
  targetIndex: number,
  maxSearchRadius: number = 50
) {
  // Clamp target index to valid range
  const clampedIndex = Math.max(
    0,
    Math.min(targetIndex, framesStore.length - 1)
  );

  // Check if exact frame exists
  if (framesStore[clampedIndex]) {
    return {frame: framesStore[clampedIndex], index: clampedIndex};
  }

  // Search in expanding radius
  let searchRadius = 1;
  while (searchRadius <= maxSearchRadius) {
    // Check frames before target index
    const beforeIndex = clampedIndex - searchRadius;
    if (beforeIndex >= 0 && framesStore[beforeIndex]) {
      return {frame: framesStore[beforeIndex], index: beforeIndex};
    }

    // Check frames after target index
    const afterIndex = clampedIndex + searchRadius;
    if (afterIndex < framesStore.length && framesStore[afterIndex]) {
      return {frame: framesStore[afterIndex], index: afterIndex};
    }

    searchRadius++;
  }

  return null;
}

/**
 * Calculate canvas dimensions based on video orientation and window size
 * @param orientation - 'portrait' or 'landscape'
 * @param tourWidth - Original video width
 * @param tourHeight - Original video height
 * @param windowWidth - Current window width
 * @param windowHeight - Current window height
 * @param isDesktop - Whether running on desktop
 * @param fullScreen - Whether in fullscreen mode
 * @returns Object with calculated canvas dimensions
 */
export function calculateCanvasDimensions(
  orientation: string,
  tourWidth: number,
  tourHeight: number,
  windowWidth: number,
  windowHeight: number,
  isDesktop: boolean,
  fullScreen: boolean = false
) {
  let canvasHeight: number;
  let canvasWidth: number;

  const aspectRatio = tourWidth / tourHeight;

  if (fullScreen) {
    if (orientation === 'portrait') {
      canvasHeight = windowHeight;
      canvasWidth = canvasHeight * aspectRatio;

      if (canvasWidth > windowWidth) {
        canvasWidth = windowWidth;
        canvasHeight = canvasWidth / aspectRatio;
      }
    } else {
      canvasWidth = windowWidth;
      canvasHeight = canvasWidth / aspectRatio;

      if (canvasHeight > windowHeight) {
        canvasHeight = windowHeight;
        canvasWidth = canvasHeight * aspectRatio;
      }
    }
    return {canvasHeight, canvasWidth};
  }

  if (orientation === 'portrait') {
    canvasHeight = windowHeight;
    canvasWidth = aspectRatio * windowHeight;
    if (windowWidth > canvasWidth && windowHeight > windowWidth) {
      canvasWidth = windowWidth;
      canvasHeight = canvasWidth / aspectRatio;
    }
  } else {
    canvasWidth = windowWidth;
    canvasHeight = canvasWidth / aspectRatio;
  }

  if ((canvasWidth > 640 || canvasHeight > 960) && isDesktop) {
    canvasHeight = tourHeight * 0.5;
    canvasWidth = tourWidth * 0.5;
  }

  if (canvasWidth > 650) {
    canvasWidth = 650;
    canvasHeight = canvasWidth / aspectRatio;
  }

  return {canvasHeight, canvasWidth};
}

/**
 * Extract clip numbers from URL paths
 * Handles different URL formats: h4.webm, l11.webm, _m0.webm, _m1.webm
 * @param urls - Array of clip URLs
 * @returns Array of clip numbers
 */
export function extractClipNumbers(urls: string[]): number[] {
  return urls.map((url) => {
    const match = url.match(/[_]?[hlm](\d+)\.webm/);
    return match ? parseInt(match[1], 10) : 0;
  });
}

/**
 * Calculate frame offsets for clips in a worker's chunk
 * @param clipIndices - Array of clip indices for this worker
 * @param allClips - All clip information with frame counts
 * @returns Array of objects with clip index and frame offset
 */
export function calculateClipFrameOffsets(
  clipIndices: number[],
  allClips: ClipInfo[]
) {
  return clipIndices.map((clipIndex) => {
    let frameOffset = 0;
    // Sum up frame counts for all clips that come before this clip
    for (let j = 0; j < allClips.length; j++) {
      if (allClips[j].index < clipIndex) {
        frameOffset += allClips[j].frameCount;
      }
    }
    return {clipIndex, frameOffset};
  });
}
